import React, { useState, useEffect } from 'react';
import { usageLogApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import { Link } from 'react-router-dom';
import {
  Activity,
  CreditCard,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Filter,
  ChevronRight,
  ChevronLeft,
  User,
  Zap,
  Mail,
  FileText,
  BarChart3,
  TrendingUp,
  Shield
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { toast } from 'sonner';

export default function UsageLogPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const { selectedProduct, products } = useProduct();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action_type: 'all',
    trigger_source: 'all',
    status: 'all',
    agent_type: 'all'
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, selectedProduct]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 25 };
      if (filters.action_type && filters.action_type !== 'all') params.action_type = filters.action_type;
      if (filters.trigger_source && filters.trigger_source !== 'all') params.trigger_source = filters.trigger_source;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.agent_type && filters.agent_type !== 'all') params.agent_type = filters.agent_type;
      if (selectedProduct && selectedProduct !== 'all') params.product_id = selectedProduct;

      const [logsRes, summaryRes] = await Promise.all([
        usageLogApi.list(params),
        usageLogApi.getSummary(30)
      ]);
      
      setLogs(logsRes.data.items);
      setTotalPages(logsRes.data.pages);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load usage logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-emerald-100 text-emerald-800',
      failed: 'bg-red-100 text-red-800',
      blocked_by_cap: 'bg-amber-100 text-amber-800',
      skipped_duplicate: 'bg-slate-100 text-slate-800'
    };
    const labels = {
      success: 'Success',
      failed: 'Failed',
      blocked_by_cap: 'Blocked by Cap',
      skipped_duplicate: 'Skipped (Duplicate)'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'scheduling': return <Calendar className="w-4 h-4" />;
      case 'follow_up': return <Mail className="w-4 h-4" />;
      case 'reminder_24h': return <Clock className="w-4 h-4" />;
      case 'reminder_1h': return <Clock className="w-4 h-4" />;
      case 'proposal_follow_up': return <FileText className="w-4 h-4" />;
      case 'call_analysis': return <Activity className="w-4 h-4" />;
      case 'proposal': return <FileText className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getTriggerBadge = (source) => {
    const styles = {
      manual: 'bg-blue-100 text-blue-800',
      scheduler: 'bg-purple-100 text-purple-800',
      api: 'bg-slate-100 text-slate-800'
    };
    return <Badge className={styles[source] || 'bg-slate-100'}>{source}</Badge>;
  };

  if (loading && !logs.length) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
          <div className="h-96 bg-slate-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="usage-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">AI Usage Log</h1>
          <p className="text-slate-500 mt-1">Track all AI credit usage for transparency and debugging</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2" data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Usage</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.today_credits_used}</p>
                  <p className="text-xs text-slate-400">of {summary.daily_credit_cap} cap</p>
                </div>
                <CreditCard className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Cap Remaining</p>
                  <p className="text-2xl font-bold text-emerald-600">{summary.daily_cap_remaining}</p>
                  <p className="text-xs text-slate-400">credits today</p>
                </div>
                <Shield className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">30-Day Total</p>
                  <p className="text-2xl font-bold text-purple-600">{summary.total_credits_used}</p>
                  <p className="text-xs text-slate-400">credits used</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Success Rate</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {summary.by_status?.success 
                      ? Math.round((summary.by_status.success / Object.values(summary.by_status).reduce((a, b) => a + b, 0)) * 100)
                      : 100}%
                  </p>
                  <p className="text-xs text-slate-400">actions succeeded</p>
                </div>
                <BarChart3 className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Usage Breakdown */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* By Action Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By Action Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.by_action_type || {}).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getActionIcon(type)}
                      <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{data.credits} credits</span>
                      <span className="text-sm text-slate-500 ml-2">({data.count} actions)</span>
                    </div>
                  </div>
                ))}
                {Object.keys(summary.by_action_type || {}).length === 0 && (
                  <p className="text-slate-500 text-center py-4">No usage data</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {status === 'success' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      {status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                      {status === 'blocked_by_cap' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                      {status === 'skipped_duplicate' && <Shield className="w-4 h-4 text-slate-500" />}
                      <span className="capitalize">{status.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(summary.by_status || {}).length === 0 && (
                  <p className="text-slate-500 text-center py-4">No status data</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-4 h-4 text-slate-500" />
            
            <Select 
              value={filters.action_type} 
              onValueChange={(v) => { setFilters({...filters, action_type: v}); setPage(1); }}
            >
              <SelectTrigger className="w-48" data-testid="filter-action-type">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="scheduling">Scheduling</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
                <SelectItem value="reminder_24h">24h Reminder</SelectItem>
                <SelectItem value="reminder_1h">1h Reminder</SelectItem>
                <SelectItem value="proposal_follow_up">Proposal Follow-up</SelectItem>
                <SelectItem value="call_analysis">Call Analysis</SelectItem>
                <SelectItem value="proposal">Proposal</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.trigger_source} 
              onValueChange={(v) => { setFilters({...filters, trigger_source: v}); setPage(1); }}
            >
              <SelectTrigger className="w-48" data-testid="filter-trigger">
                <SelectValue placeholder="Trigger Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="scheduler">Scheduler</SelectItem>
                <SelectItem value="api">API</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.status} 
              onValueChange={(v) => { setFilters({...filters, status: v}); setPage(1); }}
            >
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="blocked_by_cap">Blocked by Cap</SelectItem>
                <SelectItem value="skipped_duplicate">Skipped Duplicate</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.agent_type}
              onValueChange={(v) => { setFilters({...filters, agent_type: v}); setPage(1); }}
            >
              <SelectTrigger className="w-52" data-testid="filter-agent-type">
                <SelectValue placeholder="Agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                <SelectItem value="lead_intake">Lead Intake</SelectItem>
                <SelectItem value="qualification">Qualification</SelectItem>
                <SelectItem value="product_fit">Product Fit</SelectItem>
                <SelectItem value="trial_follow_up">Trial Follow-up</SelectItem>
                <SelectItem value="booking">Booking</SelectItem>
                <SelectItem value="discovery">Discovery</SelectItem>
                <SelectItem value="proposal">Proposal</SelectItem>
                <SelectItem value="objection_handling">Objection Handling</SelectItem>
                <SelectItem value="human_handoff">Human Handoff</SelectItem>
                <SelectItem value="closing_follow_up">Closing Follow-up</SelectItem>
              </SelectContent>
            </Select>

            {(filters.action_type !== 'all' || filters.trigger_source !== 'all' || filters.status !== 'all' || filters.agent_type !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setFilters({ action_type: 'all', trigger_source: 'all', status: 'all', agent_type: 'all' }); setPage(1); }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Usage History</CardTitle>
          <CardDescription>Detailed log of all AI credit-consuming actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>AI Reason</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Request ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap">
                      <div className="text-sm">
                        <div>{formatDate(log.timestamp_utc)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.lead_id ? (
                        <Link 
                          to={`/leads/${log.lead_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          <div className="font-medium">{log.lead_name || 'Unknown'}</div>
                          <div className="text-xs text-slate-500">{log.lead_email}</div>
                        </Link>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.product_name ? (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                          {log.product_name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.agent_type ? (
                        <span className="text-xs capitalize text-purple-700">
                          {String(log.agent_type).replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action_type)}
                        <span className="capitalize">{log.action_type?.replace(/_/g, ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {log.ai_reason ? (
                        <div
                          className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 line-clamp-2"
                          title={log.ai_reason}
                          data-testid={`usage-ai-reason-${log.id}`}
                        >
                          {log.ai_reason}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {getTriggerBadge(log.trigger_source)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {log.credits_charged > 0 ? log.credits_charged : '-'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.status)}
                      {log.error_message && (
                        <div className="text-xs text-red-600 mt-1 max-w-32 truncate" title={log.error_message}>
                          {log.error_message}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.user_name ? (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span className="text-sm">{log.user_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
                        {log.request_id?.slice(0, 8)}...
                      </code>
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      No usage logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
